import type { z } from "zod";
import {
  agentsPageSchema,
  alertRoutesPageSchema,
  alertRulesPageSchema,
  alertsPageSchema,
  assetsPageSchema,
  auditExportListSchema,
  auditListPageSchema,
  auditVerifyResultSchema,
  checkObservationsPageSchema,
  checkDefinitionSchema,
  checksPageSchema,
  createScopeResultSchema,
  eventsPageSchema,
  incidentDetailSchema,
  incidentSchema,
  incidentsPageSchema,
  loginResponseSchema,
  maintenanceWindowsPageSchema,
  meResponseSchema,
  reportSchema,
  reportsPageSchema,
  scopesPageSchema,
  slosPageSchema,
  sloStatusResponseSchema,
  statusPagesPageSchema,
  tenantsListSchema,
  timelinePageSchema,
  workspacesListSchema,
} from "@ghostwatch/schemas";
import type {
  AgentSummary,
  Alert,
  AlertRoute,
  AlertRule,
  Asset,
  AuditExportItem,
  AuditListItem,
  AuditVerifyResult,
  CheckConfig,
  CheckDefinition,
  CheckObservation,
  CheckSummary,
  CreateScopeResult,
  Incident,
  IncidentDetail,
  IncidentStatus,
  LoginResponse,
  MaintenanceWindow,
  MeResponse,
  MonitorScope,
  Page,
  Report,
  Slo,
  SloStatusResponse,
  StatusPage,
  StoredEvent,
  Tenant,
  TimelineEvent,
  Uuid,
  Workspace,
} from "@ghostwatch/shared-types";
import { ApiError } from "./errors";

export interface GhostWatchClientOptions {
  baseUrl: string;
  token?: string | null;
  workspaceId?: string | null;
  tenantId?: string | null;
}

export interface PageParams {
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  /** When set, the JSON response is runtime-validated against this schema before returning. */
  schema?: z.ZodTypeAny;
  /** Skip Authorization header (only `login` needs this). */
  anonymous?: boolean;
}

/**
 * Typed fetch client for the GhostWatch control-plane REST API (`docs/development/BUILD-PLAN.md`
 * §5). Every method sends `Authorization: Bearer <token>` (except `login`) and `X-Workspace-Id`
 * when a workspace is set; every failure — HTTP error, network failure, or a response that
 * doesn't match the expected shape — surfaces as a single {@link ApiError}.
 */
export class GhostWatchClient {
  private baseUrl: string;
  private token: string | null;
  private workspaceId: string | null;
  private tenantId: string | null;

  constructor(baseUrl: string, token?: string | null, workspaceId?: string | null, tenantId?: string | null) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.token = token ?? null;
    this.workspaceId = workspaceId ?? null;
    this.tenantId = tenantId ?? null;
  }

  static fromOptions(opts: GhostWatchClientOptions): GhostWatchClient {
    return new GhostWatchClient(opts.baseUrl, opts.token, opts.workspaceId, opts.tenantId);
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  setWorkspaceId(workspaceId: string | null): void {
    this.workspaceId = workspaceId;
  }

  setTenantId(tenantId: string | null): void {
    this.tenantId = tenantId;
  }

  get currentToken(): string | null {
    return this.token;
  }

  get currentWorkspaceId(): string | null {
    return this.workspaceId;
  }

  // -------------------------------------------------------------------
  // Core request plumbing
  // -------------------------------------------------------------------

  private async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const url = new URL(this.baseUrl + path, typeof window !== "undefined" ? window.location.href : undefined);
    if (opts.query) {
      for (const [key, value] of Object.entries(opts.query)) {
        if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
      }
    }

    const headers: Record<string, string> = { Accept: "application/json" };
    if (!opts.anonymous && this.token) headers.Authorization = `Bearer ${this.token}`;
    if (this.workspaceId) headers["X-Workspace-Id"] = this.workspaceId;
    if (this.tenantId) headers["X-Tenant-Id"] = this.tenantId;
    let body: string | undefined;
    if (opts.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(opts.body);
    }

    let response: Response;
    try {
      response = await fetch(url.toString(), {
        method: opts.method ?? "GET",
        headers,
        body,
        signal: opts.signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      throw new ApiError(0, {
        error: "network_error",
        detail: err instanceof Error ? err.message : "the request could not be sent",
      });
    }

    if (!response.ok) {
      let parsed: { error?: string; detail?: string | null } = {};
      try {
        parsed = await response.json();
      } catch {
        // Body wasn't JSON (e.g. a proxy error page) — fall through with the status text.
      }
      throw new ApiError(response.status, {
        error: parsed.error ?? `http_${response.status}`,
        detail: parsed.detail ?? (parsed.error ? null : response.statusText || null),
      });
    }

    if (response.status === 204) return undefined as T;
    const json = await response.json();
    if (opts.schema) {
      const result = opts.schema.safeParse(json);
      if (!result.success) {
        throw new ApiError(0, {
          error: "invalid_response_shape",
          detail: result.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`).join("; "),
        });
      }
      return result.data as T;
    }
    return json as T;
  }

  private async requestBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
    const url = this.baseUrl + path;
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    if (this.workspaceId) headers["X-Workspace-Id"] = this.workspaceId;
    if (this.tenantId) headers["X-Tenant-Id"] = this.tenantId;
    let response: Response;
    try {
      response = await fetch(url, { method: opts.method ?? "GET", headers, signal: opts.signal });
    } catch (err) {
      throw new ApiError(0, {
        error: "network_error",
        detail: err instanceof Error ? err.message : "the request could not be sent",
      });
    }
    if (!response.ok) {
      let parsed: { error?: string; detail?: string | null } = {};
      try {
        parsed = await response.json();
      } catch {
        // non-JSON error body
      }
      throw new ApiError(response.status, {
        error: parsed.error ?? `http_${response.status}`,
        detail: parsed.detail ?? null,
      });
    }
    return response.blob();
  }

  private async requestText(path: string, opts: RequestOptions = {}): Promise<string> {
    const url = this.baseUrl + path;
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    let response: Response;
    try {
      response = await fetch(url, { method: "GET", headers, signal: opts.signal });
    } catch (err) {
      throw new ApiError(0, {
        error: "network_error",
        detail: err instanceof Error ? err.message : "the request could not be sent",
      });
    }
    if (!response.ok) {
      throw new ApiError(response.status, { error: `http_${response.status}`, detail: response.statusText || null });
    }
    return response.text();
  }

  private pageQuery(params?: PageParams) {
    return { limit: params?.limit, offset: params?.offset };
  }

  // -------------------------------------------------------------------
  // Auth
  // -------------------------------------------------------------------

  login(email: string, password: string): Promise<LoginResponse> {
    return this.request("/v1/auth/login", {
      method: "POST",
      body: { email, password },
      anonymous: true,
      schema: loginResponseSchema,
    });
  }

  logout(): Promise<{ ok: boolean }> {
    return this.request("/v1/auth/logout", { method: "POST" });
  }

  me(signal?: AbortSignal): Promise<MeResponse> {
    return this.request("/v1/auth/me", { schema: meResponseSchema, signal });
  }

  // -------------------------------------------------------------------
  // Tenants / workspaces (Settings)
  // -------------------------------------------------------------------

  listTenants(signal?: AbortSignal): Promise<{ items: Tenant[] }> {
    return this.request("/v1/tenants", { schema: tenantsListSchema, signal });
  }

  listWorkspaces(signal?: AbortSignal): Promise<{ items: Workspace[] }> {
    return this.request("/v1/workspaces", { schema: workspacesListSchema, signal });
  }

  // -------------------------------------------------------------------
  // Monitor scopes
  // -------------------------------------------------------------------

  listScopes(params?: PageParams): Promise<Page<MonitorScope>> {
    return this.request("/v1/scopes", { query: this.pageQuery(params), schema: scopesPageSchema, signal: params?.signal });
  }

  createScope(body: {
    scope_type: string;
    value: string;
    verification_method: string;
    expires_at?: string;
  }): Promise<CreateScopeResult> {
    return this.request("/v1/scopes", { method: "POST", body, schema: createScopeResultSchema });
  }

  verifyScope(id: Uuid): Promise<{ status: string }> {
    return this.request(`/v1/scopes/${id}/verify`, { method: "POST" });
  }

  revokeScope(id: Uuid): Promise<{ status: string }> {
    return this.request(`/v1/scopes/${id}/revoke`, { method: "POST" });
  }

  // -------------------------------------------------------------------
  // Assets
  // -------------------------------------------------------------------

  listAssets(params?: PageParams): Promise<Page<Asset>> {
    return this.request("/v1/assets", { query: this.pageQuery(params), schema: assetsPageSchema, signal: params?.signal });
  }

  getAsset(id: Uuid, signal?: AbortSignal): Promise<Asset> {
    return this.request(`/v1/assets/${id}`, { signal });
  }

  createAsset(body: {
    asset_type: string;
    display_name: string;
    identifier: string;
    criticality?: string;
    environment?: string;
    scope_id?: string;
    tags?: string[];
  }): Promise<{ id: Uuid }> {
    return this.request("/v1/assets", { method: "POST", body });
  }

  updateAsset(
    id: Uuid,
    body: Partial<{ display_name: string; criticality: string; environment: string; tags: string[]; status: string }>,
  ): Promise<{ ok: boolean }> {
    return this.request(`/v1/assets/${id}`, { method: "PATCH", body });
  }

  deleteAsset(id: Uuid): Promise<{ ok: boolean }> {
    return this.request(`/v1/assets/${id}`, { method: "DELETE" });
  }

  // -------------------------------------------------------------------
  // Agents
  // -------------------------------------------------------------------

  listAgents(params?: PageParams): Promise<Page<AgentSummary>> {
    return this.request("/v1/agents", { query: this.pageQuery(params), schema: agentsPageSchema, signal: params?.signal });
  }

  createEnrollToken(): Promise<{ token: string; expires_in_hours: number }> {
    return this.request("/v1/agents/enroll-tokens", { method: "POST" });
  }

  revokeAgent(id: Uuid): Promise<{ ok: boolean }> {
    return this.request(`/v1/agents/${id}/revoke`, { method: "POST" });
  }

  // -------------------------------------------------------------------
  // Checks
  // -------------------------------------------------------------------

  listChecks(params?: PageParams): Promise<Page<CheckSummary>> {
    return this.request("/v1/checks", { query: this.pageQuery(params), schema: checksPageSchema, signal: params?.signal });
  }

  getCheck(id: Uuid, signal?: AbortSignal): Promise<CheckDefinition> {
    return this.request(`/v1/checks/${id}`, { schema: checkDefinitionSchema, signal });
  }

  createCheck(body: {
    asset_id: Uuid;
    name: string;
    configuration: CheckConfig;
    interval_seconds?: number;
    timeout_seconds?: number;
    failure_threshold?: number;
    recovery_threshold?: number;
  }): Promise<{ id: Uuid }> {
    return this.request("/v1/checks", { method: "POST", body });
  }

  updateCheck(
    id: Uuid,
    body: Partial<{
      name: string;
      enabled: boolean;
      interval_seconds: number;
      timeout_seconds: number;
      configuration: CheckConfig;
    }>,
  ): Promise<{ ok: boolean }> {
    return this.request(`/v1/checks/${id}`, { method: "PATCH", body });
  }

  deleteCheck(id: Uuid): Promise<{ ok: boolean }> {
    return this.request(`/v1/checks/${id}`, { method: "DELETE" });
  }

  runCheck(id: Uuid): Promise<{ queued: boolean; job_id: string }> {
    return this.request(`/v1/checks/${id}/run`, { method: "POST" });
  }

  checkObservations(id: Uuid, params?: PageParams): Promise<Page<CheckObservation>> {
    return this.request(`/v1/checks/${id}/observations`, {
      query: this.pageQuery(params),
      schema: checkObservationsPageSchema,
      signal: params?.signal,
    });
  }

  // -------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------

  listEvents(params?: PageParams): Promise<Page<StoredEvent>> {
    return this.request("/v1/events", { query: this.pageQuery(params), schema: eventsPageSchema, signal: params?.signal });
  }

  eventPayload(id: Uuid): Promise<{ payload: unknown }> {
    return this.request(`/v1/events/${id}/payload`);
  }

  // -------------------------------------------------------------------
  // Alerts, rules, routes
  // -------------------------------------------------------------------

  listAlerts(params?: PageParams): Promise<Page<Alert>> {
    return this.request("/v1/alerts", { query: this.pageQuery(params), schema: alertsPageSchema, signal: params?.signal });
  }

  ackAlert(id: Uuid): Promise<{ ok: boolean }> {
    return this.request(`/v1/alerts/${id}/ack`, { method: "POST" });
  }

  listAlertRules(params?: PageParams): Promise<Page<AlertRule>> {
    return this.request("/v1/alerts/rules", {
      query: this.pageQuery(params),
      schema: alertRulesPageSchema,
      signal: params?.signal,
    });
  }

  createAlertRule(body: {
    name: string;
    condition: unknown;
    severity: string;
    dedupe_key_template?: string;
    for_duration_seconds?: number;
    route_id?: string;
    maintenance_window_policy?: string;
    asset_filter?: unknown;
  }): Promise<{ id: Uuid }> {
    return this.request("/v1/alerts/rules", { method: "POST", body });
  }

  updateAlertRule(id: Uuid, body: Partial<{ enabled: boolean; severity: string; route_id: string }>): Promise<{ ok: boolean }> {
    return this.request(`/v1/alerts/rules/${id}`, { method: "PATCH", body });
  }

  deleteAlertRule(id: Uuid): Promise<{ ok: boolean }> {
    return this.request(`/v1/alerts/rules/${id}`, { method: "DELETE" });
  }

  listRoutes(params?: PageParams): Promise<Page<AlertRoute>> {
    return this.request("/v1/routes", { query: this.pageQuery(params), schema: alertRoutesPageSchema, signal: params?.signal });
  }

  createRoute(body: {
    name: string;
    channel_type: string;
    destination: unknown;
    severity_filter?: string[];
    include_payload?: boolean;
    schedule_id?: string;
    escalation_policy_id?: string;
  }): Promise<{ id: Uuid }> {
    return this.request("/v1/routes", { method: "POST", body });
  }

  deleteRoute(id: Uuid): Promise<{ ok: boolean }> {
    return this.request(`/v1/routes/${id}`, { method: "DELETE" });
  }

  testRoute(id: Uuid): Promise<{ queued: boolean }> {
    return this.request(`/v1/routes/${id}/test`, { method: "POST" });
  }

  // -------------------------------------------------------------------
  // Incidents
  // -------------------------------------------------------------------

  listIncidents(params?: PageParams): Promise<Page<Incident>> {
    return this.request("/v1/incidents", {
      query: this.pageQuery(params),
      schema: incidentsPageSchema,
      signal: params?.signal,
    });
  }

  getIncident(id: Uuid, signal?: AbortSignal): Promise<IncidentDetail> {
    return this.request(`/v1/incidents/${id}`, { schema: incidentDetailSchema, signal });
  }

  createIncident(body: { title: string; severity: string; asset_ids?: Uuid[]; summary?: string }): Promise<{ id: Uuid }> {
    return this.request("/v1/incidents", { method: "POST", body });
  }

  commentIncident(id: Uuid, text: string): Promise<{ ok: boolean; status: string }> {
    return this.request(`/v1/incidents/${id}/comment`, { method: "POST", body: { text } });
  }

  transitionIncident(id: Uuid, to: IncidentStatus): Promise<Incident> {
    return this.request(`/v1/incidents/${id}/status`, { method: "POST", body: { to }, schema: incidentSchema });
  }

  incidentTimeline(id: Uuid, params?: PageParams): Promise<Page<TimelineEvent>> {
    return this.request(`/v1/incidents/${id}/timeline`, {
      query: this.pageQuery(params),
      schema: timelinePageSchema,
      signal: params?.signal,
    });
  }

  // -------------------------------------------------------------------
  // Maintenance windows
  // -------------------------------------------------------------------

  listWindows(params?: PageParams): Promise<Page<MaintenanceWindow>> {
    return this.request("/v1/maintenance-windows", {
      query: this.pageQuery(params),
      schema: maintenanceWindowsPageSchema,
      signal: params?.signal,
    });
  }

  createWindow(body: {
    name: string;
    timezone: string;
    start_time: string;
    end_time: string;
    days_of_week?: number[];
    asset_filter?: unknown;
    suppress_alerts?: boolean;
    show_status_page?: boolean;
  }): Promise<{ id: Uuid }> {
    return this.request("/v1/maintenance-windows", { method: "POST", body });
  }

  deleteWindow(id: Uuid): Promise<{ ok: boolean }> {
    return this.request(`/v1/maintenance-windows/${id}`, { method: "DELETE" });
  }

  // -------------------------------------------------------------------
  // SLOs
  // -------------------------------------------------------------------

  listSlos(params?: PageParams): Promise<Page<Slo>> {
    return this.request("/v1/slos", { query: this.pageQuery(params), schema: slosPageSchema, signal: params?.signal });
  }

  createSlo(body: {
    asset_id: Uuid;
    name: string;
    objective_type: string;
    target_percentage: number;
    window_days?: number;
    good_event_query: string;
    total_event_query: string;
    burn_rate_alerts?: { factor: number; window_hours: number }[];
  }): Promise<{ id: Uuid }> {
    return this.request("/v1/slos", { method: "POST", body });
  }

  deleteSlo(id: Uuid): Promise<{ ok: boolean }> {
    return this.request(`/v1/slos/${id}`, { method: "DELETE" });
  }

  sloStatus(id: Uuid, signal?: AbortSignal): Promise<SloStatusResponse> {
    return this.request(`/v1/slos/${id}/status`, { schema: sloStatusResponseSchema, signal });
  }

  // -------------------------------------------------------------------
  // Status pages
  // -------------------------------------------------------------------

  listStatusPages(params?: PageParams): Promise<Page<StatusPage>> {
    return this.request("/v1/status-pages", {
      query: this.pageQuery(params),
      schema: statusPagesPageSchema,
      signal: params?.signal,
    });
  }

  createStatusPage(body: {
    slug: string;
    title: string;
    visibility: string;
    service_groups?: { name: string; asset_ids: Uuid[] }[];
  }): Promise<{ id: Uuid }> {
    return this.request("/v1/status-pages", { method: "POST", body });
  }

  publishStatusPage(id: Uuid, published: boolean): Promise<{ ok: boolean }> {
    return this.request(`/v1/status-pages/${id}/publish`, { method: "POST", body: { published } });
  }

  publishIncidentToPage(pageId: Uuid, incidentId: Uuid, publicNote?: string): Promise<{ ok: boolean }> {
    return this.request(`/v1/status-pages/${pageId}/incidents/${incidentId}/publish`, {
      method: "POST",
      body: { public_note: publicNote || undefined },
    });
  }

  // -------------------------------------------------------------------
  // Reports
  // -------------------------------------------------------------------

  listReports(params?: PageParams): Promise<Page<Report>> {
    return this.request("/v1/reports", { query: this.pageQuery(params), schema: reportsPageSchema, signal: params?.signal });
  }

  createReport(body: { report_type: string; format: string; params?: unknown }): Promise<{ id: Uuid; status: string }> {
    return this.request("/v1/reports", { method: "POST", body });
  }

  getReport(id: Uuid, signal?: AbortSignal): Promise<Report> {
    return this.request(`/v1/reports/${id}`, { schema: reportSchema, signal });
  }

  downloadReport(id: Uuid): Promise<Blob> {
    return this.requestBlob(`/v1/reports/${id}/download`);
  }

  // -------------------------------------------------------------------
  // Audit
  // -------------------------------------------------------------------

  listAudit(params?: PageParams): Promise<{ items: AuditListItem[]; next_cursor: string | null }> {
    return this.request("/v1/audit", { query: this.pageQuery(params), schema: auditListPageSchema, signal: params?.signal }) as Promise<{
      items: AuditListItem[];
      next_cursor: string | null;
    }>;
  }

  verifyAuditChain(): Promise<AuditVerifyResult> {
    return this.request("/v1/audit/verify", { method: "POST", schema: auditVerifyResultSchema });
  }

  exportAudit(signal?: AbortSignal): Promise<{ items: AuditExportItem[] }> {
    return this.request("/v1/audit/export", { schema: auditExportListSchema, signal });
  }

  // -------------------------------------------------------------------
  // Meta
  // -------------------------------------------------------------------

  /** Raw Prometheus text exposition — the dashboard regex-parses `ghostwatch_queue_depth`. */
  metricsText(signal?: AbortSignal): Promise<string> {
    return this.requestText("/metrics", { signal });
  }
}
